// utils/getDataFromContentful.ts
import { apiUrl } from "./env";
import { fetchWithRetry } from "./fetchWithRetry";
import { readSessionCache, writeSessionCache } from "./sessionCache";

type Props = {
    query: string;
    preview?: boolean;      // new flag
}

const CACHE_PREFIX = 'contentful_cache:';

// Same query text always means the same result within a session (preview
// mode aside), so we key purely on the query string.
const cacheKey = (query: string) => CACHE_PREFIX + query;

// Contentful credentials live in the serbeld-space-using-nodejs backend now —
// this just forwards { query, preview } to that service's /graphql endpoint,
// which already returns the { data } / { status: 'Error', ... } shape below.
// Never throws: callers can always safely read response.data / response.status.
export async function getDataFromContentful({ query, preview = false }: Props) {
    try {
        if (!query) {
            return {
                status: 'Error',
                message: 'Failed to fetch data from Contentful: Missing query parameter'
            };
        }

        if (!apiUrl) {
            return {
                status: 'Error',
                message: 'Failed to fetch data from Contentful: Missing REACT_APP_API_URL configuration'
            };
        }

        // Preview mode polls for live edits from the Contentful editor, so it
        // must always hit the network — never read or write the cache for it.
        if (!preview) {
            const cached = readSessionCache(cacheKey(query));
            if (cached) return cached;
        }

        const reqParams = JSON.stringify({ query: query.replace(/'/g, '"'), preview });

        // Transient network hiccups and 5xx/429s from the backend are retried
        // with backoff; a hung connection is aborted after timeoutMs so the UI
        // never gets stuck waiting forever on a dead request.
        const response = await fetchWithRetry(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: reqParams,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();

        // Don't cache error responses — a transient failure shouldn't stick
        // around as a false "empty" result for the rest of the session.
        if (!preview && json?.status !== 'Error') {
            writeSessionCache(cacheKey(query), json);
        }

        return json;
    } catch (error: any) {
        const message = error?.name === 'AbortError'
            ? 'Request to Contentful backend timed out'
            : (error?.message || 'Unknown error');

        return {
            status: 'Error',
            message: 'Failed to fetch data from Contentful',
            body: message
        };
    }
}